import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

// Tynn fremdriftsstripe øverst i vinduet, som i Linear, Vercel og GitHub.
// Den gir et signal med en gang man klikker, også når neste side må lastes
// ned først, og står til dataene brukeren venter på har kommet.
//
// Den regnes som «opptatt» når:
// - man har klikket en intern lenke og ruten ennå ikke har byttet,
// - en spørring henter data som ikke finnes i cachen (ikke bakgrunnsoppdatering
//   av noe som allerede vises), eller
// - noe lagres.
//
// Det som går fortere enn SHOW_DELAY, viser ingen stripe i det hele tatt.

const SHOW_DELAY = 150;
// Kort pause før stripen fullføres, så den ikke blinker i overgangen mellom
// at ruten bytter og at den nye siden starter sine spørringer.
const SETTLE_DELAY = 120;
const FINISH_DURATION = 250;
// Sikkerhetsnett hvis et klikk aldri fører til et rutebytte.
const NAV_TIMEOUT = 8000;

type Phase = "idle" | "running" | "finishing";

export const TopProgressBar = () => {
  const busy = useBusy();
  const [phase, setPhase] = useState<Phase>("idle");
  const phaseRef = useRef<Phase>("idle");
  phaseRef.current = phase;

  useEffect(() => {
    if (busy) {
      if (phaseRef.current === "running") return;
      const t = setTimeout(() => setPhase("running"), SHOW_DELAY);
      return () => clearTimeout(t);
    }
    if (phaseRef.current !== "running") return;
    const t = setTimeout(() => setPhase("finishing"), SETTLE_DELAY);
    return () => clearTimeout(t);
  }, [busy]);

  useEffect(() => {
    if (phase !== "finishing") return;
    const t = setTimeout(() => setPhase("idle"), FINISH_DURATION);
    return () => clearTimeout(t);
  }, [phase]);

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-0.5"
    >
      <div
        data-phase={phase}
        className="mimin-progress h-full origin-left bg-primary"
      />
    </div>
  );
};

const useBusy = () => {
  const waitingForData = useIsFetching({
    predicate: (query) => query.state.data === undefined,
  });
  const saving = useIsMutating();
  const navPending = useNavigationPending();
  return navPending || waitingForData > 0 || saving > 0;
};

// Rutebyttet skjer i en React-overgang, så den gamle siden blir stående til
// den nye er klar. Vi fanger derfor klikket på interne lenker selv.
const useNavigationPending = () => {
  const location = useLocation();
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const anchor = (event.target as Element | null)?.closest?.("a[href]");
      if (!anchor || anchor.getAttribute("target") === "_blank") return;
      const href = anchor.getAttribute("href") ?? "";
      if (!href.startsWith("#/") || href === window.location.hash) return;
      setPending(true);
    };
    // Capture: react-routers <Link> kaller preventDefault i sin egen handler.
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  useEffect(() => {
    setPending(false);
  }, [location.key]);

  useEffect(() => {
    if (!pending) return;
    const t = setTimeout(() => setPending(false), NAV_TIMEOUT);
    return () => clearTimeout(t);
  }, [pending]);

  return pending;
};
